'use client';

import ModalResponsive, { ModalResponsiveFooter } from '@/components/commons/modal-responsive';
import LoadingAnimationIcon from '@/components/icons/loading-animation-icon';
import CardPromotion from '@/components/sections/builder/elements/body/cart-total/component/card-promotion';
import { Button } from '@/components/ui/button';
import { useMutateValidateVoucher } from '@/hooks/api/storefront/promotion';
import { cn, isEmpty, parseErrorMessage } from '@/lib/utils';
import { ThemePromotionType } from '@/stores/builder';
import { usePromotionStore } from '@/stores/promotion';
import { StoreCart } from '@/types/http';
import { BasePromotion } from '@/types/http/promotion/common';
import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { shallow } from 'zustand/shallow';

type ModalPromotionProps = {
  openModal: boolean;
  setOpenModal: (open: boolean) => void;
  grandTotal: number;
  listPromotion: BasePromotion[] | undefined;
  onApplyVoucher: (selectedVoucherIds: string[]) => void;
  onRedeemVoucher: (promo: BasePromotion[], code: string) => void;
  token: string;
  data: StoreCart;
  theme?: ThemePromotionType;
  preview?: boolean;
};

export default function ModalPromotion({
  openModal,
  setOpenModal,
  grandTotal,
  listPromotion,
  onApplyVoucher,
  onRedeemVoucher,
  token,
  data,
  theme,
  preview = false,
}: ModalPromotionProps) {
  const [selectedPromo, setSelectedPromo] = useState<string[]>([]);
  const [voucherCode, setVoucherCode] = useState<string>('');
  const [errorVoucher, setErrorVoucher] = useState<string | null>(null);

  const promotionStore = usePromotionStore(
    (state) => ({
      selectedPromotion: state.selectedPromotion,
    }),
    shallow
  );

  const handleSelectPromo = (promoId: string, isOverlap: boolean) => {
    const isSelected = selectedPromo.includes(promoId);

    if (isSelected) {
      setSelectedPromo((prev) => prev.filter((id) => id !== promoId));
      return;
    }

    if (!isOverlap) {
      setSelectedPromo([promoId]);
    } else {
      setSelectedPromo((prev) => [...prev, promoId]);
    }
  };

  /**
   * Check if promotion is disabled
   * @param promo - Promotion
   * @returns - Boolean
   */
  const isPromoDisabled = (promo: BasePromotion): boolean => {
    const alreadySelected = selectedPromo.includes(promo.promotion_id);
    if (alreadySelected) return false;

    const selectedPromotions = (listPromotion ?? []).filter((p) =>
      selectedPromo.includes(p.promotion_id)
    );

    const hasNonStackable = selectedPromotions.some((p) => !p.is_overlap);
    const hasStackable = selectedPromotions.some((p) => p.is_overlap);

    if (grandTotal < promo.minimum_spending) return true;

    // Non-stackable promo: can't select if any promo already selected
    if (!promo.is_overlap && (hasNonStackable || hasStackable)) return true;

    // Stackable promo: can't select if any non-stackable promo already selected
    if (promo.is_overlap && hasNonStackable) return true;

    return false;
  };

  const validateVoucher = useMutateValidateVoucher({
    onSuccess: (data) => {
      const promoRewards = data.data?.promotion_rewards;
      const isValid = data.data?.is_valid;

      if (isValid && promoRewards?.length) {
        setVoucherCode('');
        setErrorVoucher(null);
        setOpenModal(false);
        onRedeemVoucher(promoRewards, voucherCode);
      } else {
        setErrorVoucher('Voucher is not valid');
      }
    },
    onError: (error) => {
      if (error instanceof AxiosError) {
        setErrorVoucher(error.response?.data?.message);
      }
    },
  });

  const handleRedeemVoucher = async () => {
    if (!voucherCode.trim()) {
      setErrorVoucher('Please enter the voucher code');
      return;
    }

    if (promotionStore.selectedPromotion.some((promo) => promo.voucher_code === voucherCode)) {
      setErrorVoucher('Voucher code already used');
      return;
    }

    const payload = {
      voucher_code: voucherCode,
      order: {
        items:
          data?.items?.map((item: { id: string; quantity: number }) => ({
            item_id: item.id,
            qty: item.quantity,
          })) || [],
        grand_total: grandTotal || 0,
      },
    };

    try {
      await validateVoucher.mutateAsync({
        body: payload,
        token,
      });
    } catch (err) {
      if (err instanceof AxiosError) {
        const rawMessage = err.response?.data?.data || err.response?.data?.message;

        try {
          const parsedMessage = JSON.parse(rawMessage);
          setErrorVoucher(
            parsedMessage.message || 'An error occurred while processing the voucher'
          );
        } catch {
          setErrorVoucher(
            parseErrorMessage(rawMessage) || 'An error occurred while processing the voucher'
          );
        }
      }
    }
  };

  useEffect(() => {
    if (openModal) {
      const selectedIds = (promotionStore.selectedPromotion ?? []).map(
        (promo) => promo.promotion_id
      );
      setSelectedPromo(selectedIds);
    } else {
      setSelectedPromo([]);
    }
  }, [openModal, promotionStore.selectedPromotion]);

  useEffect(() => {
    if (errorVoucher) {
      const timeout = setTimeout(() => {
        setErrorVoucher(null);
      }, 5000);
      return () => clearTimeout(timeout);
    }
  }, [errorVoucher]);

  return (
    <ModalResponsive
      openModal={openModal}
      title={`Select Voucher ${
        selectedPromo.length > 0 ? `(${selectedPromo.length} selected)` : ''
      }`}
      setOpenModal={setOpenModal}
      className='z-[9990] max-w-xl !rounded-none'
    >
      <div
        className='flex flex-col bg-white px-7'
        style={
          {
            '--ring-active-color': theme?.colorScheme?.background || '#000000',
            '--btn-label': theme?.colorScheme?.btnSolidlabel || '#ffffff',
            '--btn-background': theme?.colorScheme?.btnSolidBackgroud || '#000000',
          } as React.CSSProperties
        }
      >
        <div className='flex items-center w-full gap-2'>
          <div className='flex w-full gap-2'>
            <input
              type='text'
              placeholder='Enter voucher code'
              className={cn(
                'w-full flex-1 rounded p-2 text-xs outline-none ring-1 focus:ring-1',
                errorVoucher
                  ? 'ring-red-500'
                  : 'ring-gray-200 focus:ring-[var(--ring-active-color)]'
              )}
              value={voucherCode}
              onChange={(e) => setVoucherCode(e.target.value)}
            />

            <Button
              type='submit'
              variant={isEmpty(theme?.colorScheme) ? 'secondary' : null}
              className={cn(
                'flex items-center py-5 text-xs font-normal transition-all duration-300 hover:opacity-80',
                !isEmpty(theme?.colorScheme) && 'bg-[--btn-background] text-[--btn-label]'
              )}
              onClick={handleRedeemVoucher}
              disabled={validateVoucher.isPending || preview}
            >
              {validateVoucher.isPending && <LoadingAnimationIcon size={4} />} Redeem
            </Button>
          </div>
        </div>
        {errorVoucher && <p className='mt-1 text-xs text-red-500'>{errorVoucher}</p>}
      </div>
      <div className='flex max-h-[350px] flex-col gap-3 px-7 py-2'>
        <div className='pr-2 space-y-3 overflow-y-auto'>
          {(listPromotion ?? []).length > 0 ? (
            listPromotion?.map((promo) => {
              const isSelected = selectedPromo.includes(promo.promotion_id);
              const isDisabled = isPromoDisabled(promo);
              const percentage = Math.min((grandTotal / promo.minimum_spending) * 100, 100);
              const isEligiblePromo = grandTotal >= promo.minimum_spending;

              return (
                <CardPromotion
                  key={promo.promotion_id}
                  isSelected={isSelected}
                  isDisabled={isDisabled}
                  percentage={percentage}
                  promo={promo}
                  isEligiblePromo={isEligiblePromo}
                  grandTotal={grandTotal}
                  handleSelectPromo={handleSelectPromo}
                  theme={theme}
                  preview={preview}
                />
              );
            })
          ) : (
            <div className='flex items-center justify-center'>
              <div className='text-xs text-gray-500'>No vouchers can be used</div>
            </div>
          )}
        </div>
      </div>
      <ModalResponsiveFooter
        className='flex items-center !justify-between text-sm'
        style={
          {
            '--text-primary': theme?.colorScheme?.text,
            '--text-secondary': theme?.colorScheme?.textSecondary,
            '--btn-label': theme?.colorScheme?.btnSolidlabel || '#ffffff',
            '--btn-background': theme?.colorScheme?.btnSolidBackgroud || '#000000',
          } as React.CSSProperties
        }
      >
        <div className='flex justify-end w-full gap-2'>
          <Button
            variant='outline'
            onClick={() => setOpenModal(false)}
            className='text-xs font-normal'
          >
            Cancel
          </Button>
          <Button
            variant={isEmpty(theme?.colorScheme) ? 'default' : null}
            className={cn(
              'text-xs font-normal transition-all duration-300 hover:opacity-80',
              !isEmpty(theme?.colorScheme) && 'bg-[--btn-background] text-[--btn-label]'
            )}
            onClick={() => {
              onApplyVoucher(selectedPromo);
              setOpenModal(false);
            }}
            disabled={preview}
          >
            Use Voucher
          </Button>
        </div>
      </ModalResponsiveFooter>
    </ModalResponsive>
  );
}
